import { LoadingOutlined } from '@ant-design/icons';
import { Theme } from '@queimadiaria/tenant-connection-front';
import { Spin } from 'antd';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ThemeProvider } from 'styled-components';

import { getTenantTheme } from './tentantTheme';

type TenantThemeContextType = {
  theme: Theme['theme'] | null;
  loading: boolean;
};

const TenantThemeContext = createContext<TenantThemeContextType>({
  theme: null,
  loading: true,
});

export const TenantThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<Theme['theme'] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      setLoading(true);
      const themeValue = await getTenantTheme();
      setTheme(themeValue?.theme ?? null);
      setLoading(false);
    };
    fetchTheme();
  }, []);

  useEffect(() => {
    if (!theme?.logo?.favicon) return;

    let link: HTMLLinkElement | null =
      document.querySelector("link[rel*='icon']");

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = theme.logo.favicon;
    document.title = `${theme.extras.tenantName} - Admin`;
  }, [theme]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin indicator={<LoadingOutlined spin />} size='large' />
      </div>
    );
  }

  return (
    <TenantThemeContext.Provider value={{ theme, loading }}>
      <ThemeProvider theme={theme ?? ({} as Theme['theme'])}>
        {children}
      </ThemeProvider>
    </TenantThemeContext.Provider>
  );
};

export const useTenantTheme = () => useContext(TenantThemeContext);
